/* eslint-disable import/no-cycle */
import { IndicatorStudy } from '../../../Components/Charts/BaseCharts/CommodityPriceChart/CommodityPriceChartDefinitions';
import {
    AnalyticsReportType,
    CommodityMonthlyMarginModel,
    Correlation,
    Currency,
    DemeterCommodity,
    DemeterDataFrequency,
    DemeterDataSource,
    DemeterEmailSubscriptionType,
    DemeterFeatureType,
    DemeterFilterTimeSpan,
    DemeterMarket,
    DemeterMarketAccessStatus,
    DemeterPageElementType,
    DemeterPageType,
    DemeterPaymentType,
    DemeterRegion,
    DemeterSubscriptionFrequency,
    DemeterTableDefinitionType,
    DemeterUserStatus,
    DemeterUserType,
    ExchangeType,
    InsuranceState,
    LeadingIndicatorType,
    MarketIndicatorDataCombinationType,
    MarketIndicatorOutlook,
    MarketIndicatorTemplateType,
    MarketIndicatorTransformationType,
    MarketPrecision,
    SubExchangeType,
    UnitOfMeasure,
} from '../../../Generated/Raven-Demeter';
import { ChartDisplayType } from '../../Components/Charts/ChartDefinitions';
import { ChartStudyType } from '../../Components/Charts/Studies/StudiesDefinitions';
import { BasisAdjustment, BasisLagPeriod, BasisPeriod } from '../../Pages/Calculators/BasisCalculator/BasisCalculatorDefinitions';
import { OutlookAndReportsTitleEnum } from '../../Pages/OutlookAndReports/OutlookAndReportsDefinitions';

const TranslationKeyDefinition = {
    // Common translations.
    text: {
        asOfDate: '',
        averageChange: '',
        averageShort: '',
        brokerSupport: '',
        copyEmail: '',
        forwardCurve: '',
        dayMovingAverage: '',
        endDate: '',
        fiveYearAverage: '',
        helpAndPolicies: '',
        lastChange: '',
        lastUpdated: '',
        lastSavedOn: '',
        lastYear: '',
        leaveWithoutSaving: '',
        monthOverMonth: '',
        netPosition: '',
        notAvailableAbbreviation: '',
        noResults: '',
        noState: '',
        percentChange: '',
        quarterlyAndYearly: '',
        saveAs: '',
        savedBy: '',
        shortYearOverYearChange: '',
        sessionExpired: '',
        signalFrom: '',
        spotPrice: '',
        startDate: '',
        thisYear: '',
        threeYearAverage: '',
        twelveMonths: '',
        updatedBy: '',
        versesAverageChange: '',
        versesThreshold: '',
        videoGuide: '',
        videoGuides: '',
        yearToDateShort: '',
        yearlyAverage: '',
        yearOverYear: '',
        yearRange: '',
    },
    words: {
        all: '',
        actions: '',
        actual: '',
        active: '',
        addContent: '',
        and: '',
        approved: '',
        average: '',
        basis: '',
        bids: '',
        budget: '',
        by: '',
        calculate: '',
        calculated: '',
        change: '',
        chart: '',
        charts: '',
        choose: '',
        clear: '',
        commodity: '',
        contract: '',
        confidence: '',
        content: '',
        contraction: '',
        cost: '',
        country: '',
        currency: '',
        date: '',
        delay: '',
        demand: '',
        decile: '',
        default: '',
        direction: '',
        displayUnits: '',
        edit: '',
        exchange: '',
        exclude: '',
        expansion: '',
        fat: '',
        forecast: '',
        friday: '',
        fundamentals: '',
        generated: '',
        geography: '',
        here: '',
        high: '',
        historical: '',
        history: '',
        id: '',
        implied: '',
        inactive: '',
        include: '',
        indicators: '',
        inputs: '',
        licenses: '',
        lite: '',
        low: '',
        lower: '',
        management: '',
        market: '',
        mathOperation: '',
        maximum: '',
        mean: '',
        median: '',
        message: '',
        minimum: '',
        model: '',
        monday: '',
        month: '',
        monthShort: '',
        multiplier: '',
        name: '',
        none: '',
        note: '',
        of: '',
        offers: '',
        optional: '',
        other: '',
        over: '',
        overview: '',
        parent: '',
        percentChange: '',
        percentile: '',
        permissions: '',
        previousShort: '',
        previous: '',
        price: '',
        prices: '',
        probabilty: '',
        product: '',
        products: '',
        projection: '',
        protein: '',
        quarter: '',
        quarterShort: '',
        quarterFull: '',
        quarterly: '',
        range: '',
        region: '',
        rejected: '',
        relative: '',
        results: '',
        rolling: '',
        saved: '',
        sector: '',
        select: '',
        settlement: '',
        shortYearOverYear: '',
        single: '',
        spot: '',
        spotPrice: '',
        spread: '',
        standard: '',
        state: '',
        source: '',
        static: '',
        status: '',
        subtotal: '',
        supply: '',
        system: '',
        summary: '',
        table: '',
        termsAndConditions: '',
        threshold: '',
        thursday: '',
        timeFrame: '',
        title: '',
        to: '',
        tools: '',
        total: '',
        type: '',
        trades: '',
        tuesday: '',
        unit: '',
        updated: '',
        update: '',
        upper: '',
        values: '',
        volatility: '',
        volume: '',
        vs: '',
        wednesday: '',
        week: '',
        year: '',
        yearly: '',
        yield: '',
    },
    actions: {
        abandon: '',
        accept: '',
        activate: '',
        add: '',
        addAndStay: '',
        addContracts: '',
        addFiveYears: '',
        apply: '',
        back: '',
        backToTop: '',
        browseFiles: '',
        cancel: '',
        clickToDownload: '',
        close: '',
        confirm: '',
        deactivate: '',
        delete: '',
        deleteColumn: '',
        details: '',
        done: '',
        download: '',
        duplicate: '',
        edit: '',
        export: '',
        flipRegionsAndCommodities: '',
        gainInstantAccess: '',
        go: '',
        import: '',
        less: '',
        loadLastSave: '',
        logout: '',
        more: '',
        next: '',
        publish: '',
        rate: '',
        remove: '',
        removeFiveYears: '',
        renderChart: '',
        requestMoreInformation: '',
        requestPricingInformation: '',
        reset: '',
        save: '',
        saveAs: '',
        saveAndReturn: '',
        search: '',
        send: '',
        set: '',
        select: '',
        share: '',
        submit: '',
        subscribe: '',
        test: '',
        update: '',
        unpublish: '',
        unsubscribe: '',
    },
    errors: {
        apiError: '',
        componentError: '',
        dateFormatError: '',
        dateFormatWithOldestDateError: '',
        downloadSubscriptionError: '',
        pleaseContactYourBroker: '',
    },

    // Components.
    charts: {
        solidLine: {
            expansion: '',
            contraction: '',
        } as { [key in keyof CommodityMonthlyMarginModel | string]: string },
        legend: {
            history: '',
            lastMonth: '',
            lastSettlement: '',
            period: '',
        },
        limitMessage: {
            yearLimitMessage: '',
        },
        text: {
            forecastData: '',
            monthly: '',
            noChartSelected: '',
            pastTwelveMonths: '',
            projectionChartNote: '',
            seasonal: '',
            seasonalChartNote: '',
            yearOverYearChange: '',
            yearToDate: '',
        },
        displayType: {
            Area: '',
            Column: '',
            Line: '',
            Spline: '',
        } as { [key in keyof typeof ChartDisplayType]: string },
        buttons: {
            changeType: '',
            download: '',
            showModal: '',
        },
    },
    dropdown: {
        commodityChart: '',
        downloadAs: '',
        expandToFullScreen: '',
        nativeCurrency: '',
        nativeUnits: '',
        moreOptions: '',
        seasonalChart: '',
        studies: '',
        studiesLimitMessage: '',
        downloads: {
            csv: '',
            jpeg: '',
            jpg: '',
            pdf: '',
            svg: '',
            png: '',
            xlsx: '',
        },
    },
    fields: {
        chartControl: '',
        percentChange: '',
    },
    menu: {
        modal: {
            applyButtonText: '',
            cancelButtonText: '',
            commodityPreferencesHeader: '',
            commodityPreferencesAcknowledgedText: '',
            commodityPreferencesText: '',
            doThisLater: '',
            menuPreferencesHeader: '',
            menuPreferencesText: '',
            productsOverRegions: '',
            regionsOverProducts: '',
            saveAndGoButtonText: '',
            title: '',
            welcomeTitle: '',
        },
        tooltip: {
            flipRegionsAndCommodities: '',
            settings: '',
        },
        text: {
            callSupport: '',
            frequentlyAskedQuestions: '',
            frequentlyAskedQuestionsShort: '',
            support: '',
            videoGuides: '',
        },
    },
    permissions: {
        text: {
            advancedValueMatrix: '',
            farmerMarginAdministrator: '',
            hedgeMonitorProAdministrator: '',
            marketIndicatorAdministrator: '',
            marketIndicatorCompliance: '',
            marketIndicatorDeveloper: '',
            marketIndicatorSubjectMatterExpert: '',
            murexInstrumentMappingAdministrator: '',
            permissionsSuccessfullyUpdated: '',
            premiumFeatures: '',
            strategyBuilder: '',
            usersExport: '',
            users: '',
            valorization: '',
        },
        errors: {
            updatePermissionsError: '',
        },
    },
    // Major enumerations.
    analyticsReportType: {
        BackendRequests: 'BackendRequests',
        BackendUserRequests: 'BackendUserRequests',
        BackendMostActiveUsers: 'BackendMostActiveUsers',
        BackendEndpointsUsage: 'BackendEndpointsUsage',
        FrontendPageViews: 'FrontendPageViews',
        FrontendUserPageViews: 'FrontendUserPageViews',
        FrontendUserDetails: 'FrontendUserDetails',
        FrontendClicks: 'FrontendClicks',
        FrontendUserClicks: 'FrontendUserClicks',
        FrontendClicksCount: 'FrontendClicksCount',
        FrontendUserClicksCount: 'FrontendUserClicksCount',
    } as { [key in keyof typeof AnalyticsReportType | string]: string },
    commodity: {
        All: 'commodity.All',
        Aluminum: 'commodity.Aluminum',
        AnhydrousMilkFat: 'commodity.AnhydrousMilkFat',
        Butter: 'commodity.Butter',
        Butterfat: 'commodity.Butterfat',
        ButterMilkPowder: 'commodity.ButterMilkPowder',
        CattleBrahmanUnborn: 'commodity.CattleBrahmanUnborn',
        CattleBrahmanWeight1: 'commodity.CattleBrahmanWeight1',
        CattleBrahmanWeight2: 'commodity.CattleBrahmanWeight2',
        CattleCalvesUnborn: 'commodity.CattleCalvesUnborn',
        CattleCullCow: 'commodity.CattleCullCow',
        CattleDairyUnborn: 'commodity.CattleDairyUnborn',
        CattleDairyWeight1: 'commodity.CattleDairyWeight1',
        CattleDairyWeight2: 'commodity.CattleDairyWeight2',
        CattleFeeder: 'commodity.CattleFeeder',
        CattleHeiferWeight1: 'commodity.CattleHeiferWeight1',
        CattleHeiferWeight2: 'commodity.CattleHeiferWeight2',
        CattleLive: 'commodity.CattleLive',
        CattleSteerAndHeifer: 'commodity.CattleSteerAndHeifer',
        CattleSteerAndHeiferUnborn: 'commodity.CattleSteerAndHeiferUnborn',
        CattleSteerWeight1: 'commodity.CattleSteerWeight1',
        CattleSteerWeight2: 'commodity.CattleSteerWeight2',
        Cheddar: 'commodity.Cheddar',
        CheeseAmerican: 'commodity.CheeseAmerican',
        CheeseBlock: 'commodity.CheeseBlock',
        CheeseBarrel: 'commodity.CheeseBarrel',
        CheddarCurd: 'commodity.CheddarCurd',
        CheddarMild: 'commodity.CheddarMild',
        Coffee: 'commodity.Coffee',
        CoffeeArabica: 'commodity.CoffeeArabica',
        CoffeeGreen: 'commodity.CoffeeGreen',
        CoffeeRoasted: 'commodity.CoffeeRoasted',
        CoffeeRobusta: 'commodity.CoffeeRobusta',
        CoffeeSoluble: 'commodity.CoffeeSoluble',
        CrudeOil: 'commodity.CrudeOil',
        DryWhey: 'commodity.DryWhey',
        Gasoline: 'commodity.Gasoline',
        Gouda: 'commodity.Gouda',
        Lactose: 'commodity.Lactose',
        LeanHog: 'commodity.LeanHog',
        LeanHogUnborn: 'commodity.LeanHogUnborn',
        Milk: 'commodity.Milk',
        MilkClass1: 'commodity.MilkClass1',
        MilkClass2: 'commodity.MilkClass2',
        MilkClass3: 'commodity.MilkClass3',
        MilkClass4: 'commodity.MilkClass4',
        Mozzarella: 'commodity.Mozzarella',
        MilkComponents: 'commodity.MilkComponents',
        MilkEquivalent: 'commodity.MilkEquivalent',
        NaturalGas: 'commodity.NatrualGas',
        NonFatDryMilk: 'commodity.NonFatDryMilk',
        NonFatSolids: 'commodity.NonFatSolids',
        OtherSolids: 'commodity.OtherSolids',
        Protein: 'commodity.Protein',
        SkimMilkClass1: 'commodity.SkimMilkClass1',
        SkimMilkClass2: 'commodity.SkimMilkClass2',
        SkimMilkClass3: 'commodity.SkimMilkClass3',
        SkimMilkClass4: 'commodity.SkimMilkClass4',
        SkimMilkPowder: 'commodity.SkimMilkPowder',
        SkimMilkPowderLow: 'commodity.SkimMilkPowderLow',
        SkimMilkPowderMedium: 'commodity.SkimMilkPowderMedium',
        Soybean: 'commodity.Soybean',
        SoybeanMeal: 'commodity.SoybeanMeal',
        SoybeanOil: 'commodity.SoybeanOil',
        SourCream: 'commodity.SourCream',
        Sugar: 'commodity.Sugar',
        SugarRaw: 'commodity.SugarRaw',
        SugarTelQuel: 'commodity.SugarTelQuel',
        SugarWhite: 'commodity.SugarWhite',
        Unknown: 'commodity.Unknown',
        Weather: 'commodity.Weather',
        WholeMilkPowder: 'commodity.WholeMilkPowder',
    } as { [key in keyof typeof DemeterCommodity]: string },
    dataSource: {
        StoneXCalculations: '',
    } as { [key in keyof typeof DemeterDataSource]: string },
    correlation: {
        Negative: '',
        None: '',
        Positive: '',
    } as { [key in keyof typeof Correlation]: string },
    currency: {
        AUD: '',
        BRL: '',
        CAD: '',
        CNY: '',
        COP: '',
        EUR: '',
        GBP: '',
        NZD: '',
        USC: '',
        USD: '',
    } as { [key in keyof typeof Currency | string]: string },
    exchange: {
        B3: 'exchange.B3',
        Cme: 'exchange.CME',
        CME: 'exchange.CME',
        Eex: 'exchange.EEX',
        EEX: 'exchange.EEX',
        Euronext: 'exchange.Euronext',
        Dce: 'exchange.DCE',
        DCE: 'exchange.DCE',
        Ice: 'exchange.ICE',
        ICE: 'exchange.ICE',
        MDX: 'exchange.MDX',
        Mdx: 'exchange.MDX',
        Otc: 'exchange.OTC',
        OTC: 'exchange.OTC',
        Sgx: 'exchange.SGX',
        SGX: 'exchange.SGX',
        Shfe: 'exchange.SHFE',
        SHFE: 'exchange.SHFE',
        Lme: 'exchange.LME',
        LME: 'exchange.LME',
    } as { [key in keyof typeof ExchangeType | string]: string },
    subExchange: {
        Cbot: 'subExchange.CBOT',
        Comex: 'subExchange.COMEX',
        Nymex: 'subExchange.NYMEX',
        Ifeu: 'subExchange.IFEU',
        Ifsg: 'subExchange.IFSG',
        Ifus: 'subExchange.IFUS',
        Ndex: 'subExchange.NDEX',
    } as { [key in keyof typeof SubExchangeType | string]: string },
    filterTimeSpan: {
        LastTwoActuals: '',
        OneDay: '',
        ThirtyDays: '',
        OneMonth: '',
        ThreeMonths: '',
        SixMonths: '',
        NineMonths: '',
        OneYear: '',
        TwoYears: '',
        ThreeYears: '',
        FiveYears: '',
        TenYears: '',
        TwentyYears: '',
        All: '',
        YearToDate: '',
    } as { [key in keyof typeof DemeterFilterTimeSpan]: string },
    insuranceState: {
        Alabama: '',
        Alaska: '',
        Arizona: '',
        Arkansas: '',
        California: '',
        Colorado: '',
        Connecticut: '',
        Delaware: '',
        Florida: '',
        Georgia: '',
        Hawaii: '',
        Idaho: '',
        Illinois: '',
        Indiana: '',
        Iowa: '',
        Kansas: '',
        Kentucky: '',
        Louisiana: '',
        Maine: '',
        Maryland: '',
        Massachusetts: '',
        Michigan: '',
        Minnesota: '',
        Mississippi: '',
        Missouri: '',
        Montana: '',
        Nebraska: '',
        Nevada: '',
        NewHampshire: '',
        NewJersey: '',
        NewMexico: '',
        NewYork: '',
        NorthCarolina: '',
        NorthDakota: '',
        Ohio: '',
        Oklahoma: '',
        Oregon: '',
        Pennsylvania: '',
        RhodeIsland: '',
        SouthCarolina: '',
        SouthDakota: '',
        Tennessee: '',
        Texas: '',
        Utah: '',
        Vermont: '',
        Virginia: '',
        Washington: '',
        WestVirginia: '',
        Wisconsin: '',
        Wyoming: '',
    } as { [key in keyof typeof InsuranceState]: string },

    market: {
        All: '',
        Currency: '',
        Dairy: '',
        Energy: '',
        Grains: '',
        Livestock: '',
        Other: '',
        Softs: '',
        Sugar: '',
        Coffee: '',
        Cocoa: '',
        Metals: '',
    } as { [key in keyof typeof DemeterMarket | string]: string },
    marketIndicatorOutlook: {
        Unavailable: '',
        VeryBearish: '',
        Bearish: '',
        Flat: '',
        Bullish: '',
        VeryBullish: '',
    } as { [key in keyof typeof MarketIndicatorOutlook]: string },
    marketIndicatorTemplateType: {
        AverageSeasonal: '',
        AverageCount: '',
        Combination: '',
        Decile: '',
        Forecast: '',
        ForwardCurve: '',
        Historical: '',
        ManualEntry: '',
        Seasonal: '',
        SlidingWindowSeasonal: '',
        Speculative: '',
        Technical: '',
    } as { [key in keyof typeof MarketIndicatorTemplateType]: string },
    marketIndicatorDataCombinationType: {
        Aggregate: '',
        Average: '',
        Index: '',
        Multiply: '',
        Ratio: '',
        Spread: '',
    } as { [key in keyof typeof MarketIndicatorDataCombinationType]: string },
    marketIndicatorTransformationType: {
        DayOverDayChange: '',
        MonthOverMonthChange: '',
        Multiply: '',
        RelativeStrengthIndex: '',
        RollingAverage: '',
        RollingAverageForwardContracts: '',
        Trend: '',
        WeekOverWeekChange: '',
        YearOverYearChange: '',
    } as { [key in keyof typeof MarketIndicatorTransformationType]: string },
    operatorSymbol: {
        Aggregate: '',
        Average: '',
        Multiply: '',
        Index: '',
        Ratio: '',
        Spread: '',
    } as { [key in keyof typeof MarketIndicatorDataCombinationType | string]: string },
    leadingIndicatorType: {
        CommodityDailyOther: '',
        CommodityFuturesIndex: '',
        CommodityFuturesPrice: '',
        CommodityMonthlyDomestics: '',
        CommodityMonthlyExports: '',
        CommodityMonthlyImports: '',
        CommodityMonthlyPrices: '',
        CommodityMonthlyProduction: '',
        CommodityMonthlyStocks: '',
        CommodityProgress: '',
        CommoditySpotPrices: '',
        CommodityWeeklyPrices: '',
        GlobalDairyTradeWeeklyPrices: '',
    } as { [key in keyof typeof LeadingIndicatorType]: string },
    parentRegion: {
        AsiaPacific: 'parentRegion.AsiaPacific',
        America: 'parentRegion.America',
        EuropeMiddleEastAndAfrica: 'parentRegion.EuropeMiddleEastAndAfrica',
    } as { [key in keyof typeof DemeterRegion]: string },
    pageElementType: {
        BalanceTableChart: '',
        BrandCard: '',
        CommodityIndicators: '',
        CommoditySummaryTable: '',
        FuturesPricesChart: '',
        FuturesPricesComparisonChart: '',
        FuturesPricesForwardCurveChart: '',
        FuturesPricesTable: '',
        GuageChart: '',
        HedgeMonitorPro: '',
        HedgeMonitorProFullPageContent: '',
        LeadingIndicators: '',
        MarginChart: '',
        MarketIndicatorChart: '',
        MarketIndicatorChartAndFactors: '',
        MarketIndicatorCommentary: '',
        MarketIndicatorFactors: '',
        MarketIntelligenceReports: '',
        OtcPricesChart: '',
        OtcPricesForwardCurveChart: '',
        OtcPricesTable: '',
        PhysicalPricesChart: '',
        PhysicalPricesTable: '',
        PriceSummaries: '',
        ProjectionChart: '',
        SeasonalChart: '',
        ValueMatrixChart: '',
        ValueMatrixTable: '',
        ValueMatrixTableAndChart: '',
    } as { [key in keyof typeof DemeterPageElementType]: string },
    pageType: {
        BalanceTable: '',
        Dashboard: '',
        Domestics: '',
        CommodityIndicators: '',
        Exports: '',
        Futures: '',
        HedgeMonitorPro: '',
        Imports: '',
        MonitorsAndIndicators: '',
        NewsAndSummaries: '',
        PhysicalPrices: '',
        Production: '',
        Stocks: '',
        UserData: '',
        ValueMatrixCalculator: '',
    } as { [key in keyof typeof DemeterPageType]: string },
    paymentType: {
        WireTransfer: '',
        DeductionFromFuturesAccount: '',
    } as { [key in keyof typeof DemeterPaymentType]: string },
    region: {
        All: 'region.All',
        Algeria: 'region.Algeria',
        Aggregated: 'region.Aggregated',
        America: 'region.America',
        Argentina: 'region.Argentina',
        Australia: 'region.Australia',
        AsiaPacific: 'region.AsiaPacific',
        Brazil: 'region.Brazil',
        Canada: 'region.Canada',
        Chile: 'region.Chile',
        China: 'region.China',
        Colombia: 'region.Colombia',
        DominicanRepublic: 'region.DominicanRepublic',
        EuropeanUnion: 'region.EuropeanUnion',
        EuropeMiddleEastAndAfrica: 'region.EuropeMiddleEastAndAfrica',
        Expana: 'region.Expana',
        GlobalDairyTrade: 'region.GlobalDairyTrade',
        Indonesia: 'region.Indonesia',
        MajorExporters: 'region.MajorExporters',
        Malaysia: 'region.Malaysia',
        Mexico: 'region.Mexico',
        Oceania: 'region.Oceania',
        NewZealand: 'region.NewZealand',
        Philippines: 'region.Philippines',
        UnitedStates: 'region.UnitedStates',
        Uruguay: 'region.Uruguay',
    } as { [key in keyof typeof DemeterRegion]: string },
    tableDefinitionType: {
        CommodityFuturesTable: '',
        CommodityFuturesIndexTable: '',
        CommodityGlobalDairyTradeWeeklyPricesTable: '',
        CommodityMonthlyDomesticTable: '',
        CommodityMonthlyExportsTable: '',
        CommodityMonthlyImportsTable: '',
        CommodityMonthlyMarginTable: '',
        CommodityMonthlyPricesTable: '',
        CommodityMonthlyProductionTable: '',
        CommodityMonthlySalesTable: '',
        CommodityMonthlyStockTable: '',
        CommodityOtcPricesTable: '',
        CommodityOtherTable: '',
        CommodityPricesTable: '',
        CommodityProgressTable: '',
        CommoditySpotPricesTable: '',
        CommoditySummaryTable: '',
        CommodityWeeklyPricesTable: '',
        CommodityYearlyBalanceTable: '',
        MarketNews: '',
        UsersTable: '',
    } as { [key in keyof typeof DemeterTableDefinitionType]: string },
    unitOfMeasure: {
        Bag: '',
        Barrel: '',
        Bushel: '',
        Currency: '',
        EuropeanUnionAllowance: '',
        Gallon: '',
        HundredKilogram: '',
        HundredWeight: '',
        Imperial: '',
        Index: '',
        Kilogram: '',
        KilogramMilkSolids: '',
        Liter: '',
        MegaWattHour: '',
        Metric: '',
        MetricTon: '',
        MillionBritishThermalUnits: '',
        MillionBushels: '',
        MillionPounds: '',
        MillionLiters: '',
        MillionBags: '',
        Percent: '',
        Pound: '',
        ShortBarrel: '',
        ShortBushel: '',
        ShortContracts: '',
        ShortCurrency: '',
        ShortGallon: '',
        ShortHead: '',
        ShortHundredKilogram: '',
        ShortHundredWeight: '',
        ShortKilogram: '',
        ShortMegaWattHour: '',
        ShortMetricTon: '',
        ShortMillionBritishThermalUnits: '',
        ShortMillionGallons: '',
        ShortMillionPounds: '',
        ShortMillionLiters: '',
        ShortMillionBags: '',
        ShortThousandBags: '',
        ShortPercent: '',
        ShortPound: '',
        ShortPoundSingle: '',
        ShortThousandBarrels: '',
        ShortThousandBarrelsPerDay: '',
        ShortThousandHeads: '',
        ShortThousandMetricTons: '',
        ShortThousandPounds: '',
        ShortShortTon: '',
        ShortBag: '',
        ShortTon: '',
        ThousandBags: '',
        ThousandBarrels: '',
        ThousandBarrelsPerDay: '',
        ThousandMetricTons: '',
        ThousandPounds: '',
    } as { [key in keyof typeof UnitOfMeasure | string]: string },
    subscriptionFrequency: {
        Monthly: '',
        Quarterly: '',
        Yearly: '',
    } as { [key in keyof typeof DemeterSubscriptionFrequency]: string },

    // Other enumerations.
    featureType: {
        [DemeterFeatureType.Administration]: '',
        [DemeterFeatureType.ArtificialIntelligenceNewsRating]: '',
        [DemeterFeatureType.BasisCalculator]: '',
        [DemeterFeatureType.BudgetCalculator]: '',
        [DemeterFeatureType.CalendarSpread]: '',
        [DemeterFeatureType.Companies]: '',
        [DemeterFeatureType.ChartBuilder]: '',
        [DemeterFeatureType.DashboardCustomization]: '',
        [DemeterFeatureType.DownloadsExports]: '',
        [DemeterFeatureType.DownloadsGeneral]: '',
        [DemeterFeatureType.DownloadsImports]: '',
        [DemeterFeatureType.FarmerMarginCalculator]: '',
        [DemeterFeatureType.FarmerMarginCustomCalculators]: '',
        [DemeterFeatureType.HedgeMonitorPro]: '',
        [DemeterFeatureType.MarketIndicators]: '',
        [DemeterFeatureType.Markets]: '',
        [DemeterFeatureType.PositionsCalculator]: '',
        [DemeterFeatureType.PresentationTemplates]: '',
        [DemeterFeatureType.Refactors]: '',
        [DemeterFeatureType.StrategyBuilder]: '',
        [DemeterFeatureType.UserData]: '',
        [DemeterFeatureType.Users]: '',
        [DemeterFeatureType.Valorization]: '',
        [DemeterFeatureType.ValueMatrix]: '',
    } as { [key in keyof typeof IndicatorStudy | string]: string },
    indicatorStudy: {
        [IndicatorStudy.absolutePrice]: '',
        [IndicatorStudy.accelerationBands]: '',
        [IndicatorStudy.annualPercentageChange]: '',
        [IndicatorStudy.aroon]: '',
        [IndicatorStudy.bollingerBands]: '',
        [IndicatorStudy.detrendedPrice]: '',
        [IndicatorStudy.doubleExponentialMovingAverage]: '',
        [IndicatorStudy.exponentialMovingAverage]: '',
        [IndicatorStudy.linearRegression]: '',
        [IndicatorStudy.linearRegressionIntercept]: '',
        [IndicatorStudy.linearRegressionSlope]: '',
        [IndicatorStudy.movingAverage]: '',
        [IndicatorStudy.percentagePrice]: '',
        [IndicatorStudy.rateOfChange]: '',
        [IndicatorStudy.relativeStrengthIndex]: '',
        [IndicatorStudy.tema]: '',
        [IndicatorStudy.trix]: '',
        [IndicatorStudy.weightedMovingAverage]: '',
        [IndicatorStudy.aroonOscillator]: '',
        [IndicatorStudy.chandeMomentumOscillator]: '',
        [IndicatorStudy.disparityindex]: '',
    } as { [key in keyof typeof IndicatorStudy | string]: string },
    chartStudy: {
        [ChartStudyType.AbsolutePrice]: '',
        [ChartStudyType.BollingerBands]: '',
        [ChartStudyType.ExponentialMovingAverage]: '',
        [ChartStudyType.ForecastErrorRange]: '',
        [ChartStudyType.LinearRegression]: '',
        [ChartStudyType.MovingAverage]: '',
        [ChartStudyType.RelativeStrengthIndex]: '',
    } as { [key in keyof typeof ChartStudyType | string]: string },

    // Feature translations.
    globalDairyTrade: {},
    footer: {
        text: {
            allRightsReserved: '',
            disclaimer: '',
            privacyPolicy: '',
        },
    },
    // Page translations.
    page: {
        administration: '',
        artificialIntelligenceNewsRating: '',
        balanceTable: '',
        calculators: '',
        cancelTrial: '',
        chartBuilder: '',
        companies: '',
        disclaimer: '',
        domestics: '',
        exports: '',
        featureFlags: '',
        frequentlyAskedQuestions: '',
        hedgeMonitorPro: '',
        privacyPolicy: '',
        imports: '',
        marketIndicators: '',
        presentationTemplates: '',
        production: '',
        risk: '',
        userData: '',
        stocks: '',
        users: '',
    },
    accessPending: {
        title: '',
        text: {
            toRequestALicense: '',
        },
    },
    administration: {
        title: '',
    },
    balanceTable: {
        title: '',
        text: {
            chinaImportsNote: '',
        },
    },
    calculators: {
        title: '',
        text: {
            averageCheesePrice: '',
            basis: '',
            calendarSpread: '',
            calendarSpreadCalculator: '',
            cheesePricing: '',
            containsUserEnteredValue: '',
            federalMilkMarketingOrder: '',
            federalMilkMarketingOrderDescription: '',
            forwardSpreadCalculator: '',
            historicalSpread: '',
            milkPriceCalculator: '',
            noPriceData: '',
            quarterly: '',
            result: '',
            spread: '',
            valueMatrix: '',
            valorization: '',
        },
        actions: {
            resetPrices: '',
        },
        fields: {
            converted: '',
            foreignExchangeRate: '',
            period: '',
            spread: '',
        },
        valorization: {
            fields: {
                costOfProduction: '',
                energy: '',
                fat: '',
                labor: '',
                protein: '',
                maintenance: '',
                depreciation: '',
                streamName: '',
            },
            messages: {
                addCalculationEnginesInputsSuccessful: '',
                addCalculationEnginesInputsFailed: '',
                areYouSureYouWantToLeave: '',
                deleteCalculationEngineInputsSuccessful: '',
                pleaseEnterAValueBetween: '',
                updateCalculationEnginesInputsSuccessful: '',
                updateCalculationEnginesInputsFailed: '',
            },
            text: {
                addStream: '',
                costOfProductionTooltip: '',
                discardMyChanges: '',
                editStream: '',
                enterName: '',
                enterNote: '',
                howToUseModal: '',
                leavingValorization: '',
                netReturns: '',
                manageSavedModels: '',
                milkSolids: '',
                modelName: '',
                resetCustomValues: '',
                resetSpotPrices: '',
                returnToPage: '',
                settingsApplyToAllStreams: '',
                setValorizationModelAs: '',
                stream: '',
                valorizationModels: '',
                yourCustomValues: '',
            },
        },
        valueMatrix: {
            fields: {
                aboveMedian: '',
                applyProducerPriceIndex: '',
                deciles: '',
                innerRange: '',
                innerWeight: '',
                outerRange: '',
                outerWeight: '',
                producerPriceIndex: '',
                sector: '',
                stripType: '',
                termClassification: '',
                terms: '',
                weighted: '',
                yearsToLookBack: '',
            },
            messages: {
                failedCalculatorSave: '',
                successfulCalculatorSave: '',
            },
            text: {
                annual: '',
                chartControls: '',
                chartSettings: '',
                chartSettingsMessage: '',
                contract1: '',
                contract2: '',
                forwardContract: '',
                innerRangeTooltipInformation: '',
                limitMessage: '',
                numberOfSelectedDecileslimitMessage: '',
                numberOfSelectedYearslimitMessage: '',
                outerRangeTooltipInformation: '',
                producerPriceIndexTooltipInformation: '',
                quarter1: '',
                quarter2: '',
                quarter3: '',
                quarter4: '',
                savedVersionLoaded: '',
                stripTypeTooltipInformationFirstContract: '',
                stripTypeTooltipInformationSecondContract: '',
                stripTypeTooltipInformationForwardContract: '',
                stripTypeTooltipInformationTwelveMonth: '',
                summerMonths: '',
                twelveMonthStrip: '',
                winterMonths: '',
            },
        },
        basis: {
            chooseProductsHeader: '',
            chooseProducts: '',
            futures: '',
            physicalPrices: '',
            userData: '',
            productSelector: {
                header: '',
                tabs: {
                    productsTab: {
                        name: '',
                        product1: '',
                        product2: '',
                        useMyData: '',
                        search: '',
                        noData: '',
                    },
                    savedProductPairsTab: {
                        name: '',
                        product1: '',
                        product2: '',
                        dateSaved: '',
                        deleteProductPair: '',
                        confirmDeleteProductPair: '',
                    },
                },
                actions: {
                    uploadMyData: '',
                    set: '',
                    cancel: '',
                },
            },
            form: {
                product1: '',
                product2: '',
                historyStartDate: '',
                correlation: '',
                optimalLag: '',
                month: '',
                months: '',
                lag: '',
                lagPeriod: '',
                noLag: '',
                statisticalRelationship: '',
                slope: '',
                intercept: '',
                useRegression: '',
                basisSettings: '',
                basisPeriod: '',
                basisAdjustment: '',
                basisAdjustmentInformation: '',
                openAdvancedSettings: '',
                closeAdvancedSettings: '',
                selectIntervals: '',
                advancedCorrelation: '',
                lagCorrelation: '',
                forwardCurveEndDate: '',
                threeMonths: '',
                sixMonths: '',
                nineMonths: '',
                twelveMonths: '',
                fifteenMonths: '',
                eighteenMonths: '',
            },
            charts: {
                basisAnalysis: '',
                rollingBasisHistory: '',
                twelveMonthRollingCorrelation: '',
                correlation: '',
                basisHistory: '',
                regression: '',
            },
            actions: {
                selectSavedPair: '',
                saveProductPair: '',
                setProductPair: '',
            },
            basisLagPeriod: {
                MinusSixMonthLag: '',
                MinusFiveMonthLag: '',
                MinusFourMonthLag: '',
                MinusThreeMonthLag: '',
                MinusTwoMonthLag: '',
                MinusOneMonthLag: '',
                NoLag: '',
                OneMonthLag: '',
                TwoMonthLag: '',
                ThreeMonthLag: '',
                FourMonthLag: '',
                FiveMonthLag: '',
                SixMonthLag: '',
            } as { [key in keyof typeof BasisLagPeriod | string]: string },
            basisPeriod: {
                Basis: '',
                ThreeMonthBasis: '',
                SixMonthBasis: '',
                NineMonthBasis: '',
                TwelveMonthBasis: '',
            } as { [key in keyof typeof BasisPeriod | string]: string },
            basisAdjustment: {
                Average: '',
                Minimum: '',
                Maximum: '',
                Top10Percent: '',
                Top20Percent: '',
                Top30Percent: '',
                Top40Percent: '',
                Top50Percent: '',
                Bottom40Percent: '',
                Bottom30Percent: '',
                Bottom20Percent: '',
                Bottom10Percent: '',
            } as { [key in keyof typeof BasisAdjustment | string]: string },
            messages: {
                productPairSaveSuccess: '',
            },
        },
        forwardSpread: {
            yearAverageDropdown: {
                tooltip: '',
                labels: {
                    none: '',
                    oneYearAverage: '',
                    threeYearAverage: '',
                    fiveYearAverage: '',
                },
            },
        },
    },
    chartBuilder: {
        fields: {
            appearInLegendAs: '',
            axis: '',
            axisOneLabel: '',
            axisTwoLabel: '',
            chartType: '',
            dataSeries: '',
            dataSeriesScope: '',
            endDate: '',
            forecastData: '',
            frequency: '',
            lineColor: '',
            name: '',
            timespanControls: '',
            timespanOptions: '',
            timespanValues: '',
        },
        headers: {
            calculatedSeries: '',
            chartName: '',
            chartOptions: '',
            chartSettings: '',
            dataSeries: '',
            dataSource: '',
            deleteQuestion: '',
            labels: '',
            labelsOptional: '',
            math: '',
            myCharts: '',
            saveChart: '',
            series: '',
            time: '',
        },
        text: {
            addDataSeries: '',
            addDataSeriesSection: '',
            addDataSource: '',
            areYouSureDeleteChart: '',
            browseToSelect: '',
            chooseDataSeries: '',
            chooseDataSource: '',
            combineDataSources: '',
            createCalculatedSeries: '',
            createChart: '',
            createYourOwnChart: '',
            doNotChart: '',
            exampleTimespanControl: '',
            exampleTimespanOptions: '',
            includeInChart: '',
            manageSavedCharts: '',
            noRows: '',
            removeCalculatedSeries: '',
            selectTheDuration: '',
            toGetStarted: '',
            y1: '',
            y2: '',
        },
        messages: {
            listChartsFailure: '',
            getChartFailure: '',
            getChartSeriesFailure: '',
            positiveNumberFiveOrGreater: '',
            positiveNumberThreeOrGreater: '',
            positiveNumberTwoThroughFive: '',
            upsertChartSuccess: '',
            upsertChartFailure: '',
        },
    },
    marketIntelligenceReports: {
        title: '',
        headers: {
            change: '',
            contractDate: '',
            high: '',
            low: '',
            last: '',
            open: '',
            openInterest: '',
            openInterestNetChange: '',
            previousClose: '',
            settle: '',
            settleChange: '',
            volume: '',
        },
    },
    aiNewsRating: {
        priceSentiment: '',
        footnote: '',
    },
    userData: {
        startup: {
            header: '',
            info: '',
            note: '',
            upload: '',
        },
        upload: {
            header: '',
            description: '',
            chooseFile: '',
            import: '',
            invalidFile: '',
            errorIncorrectHeader: '',
            errorIncorrectData: '',
            error: '',
        },
        management: {
            header: '',
            upload: '',
            description: '',
            subheader: '',
            table: {
                nameHeader: '',
                fileNameHeader: '',
                unitHeader: '',
                currencyHeader: '',
                actionsHeader: '',
                itemsPerPage: '',
                pageOf: '',
            },
            confirmDelete: {
                header: '',
                message: '',
            },
        },
        form: {
            header: '',
            name: '',
            unit: '',
            currency: '',
            invalidName: '',
        },
        items: {
            headers: {
                header: '',
                priceSeriesName: '',
                rowsToDisplay: '',
            },
            dataitems: {
                displayingAllRows: '',
                displayingMostRecent: '',
                asOfDate: '',
                value: '',
                additionalDatesSorted: '',
            },
            actions: {
                addRow: '',
            },
            errors: {
                duplicateDate: '',
                mustBeValidDate: '',
                valueRequiredNumber: '',
                saveError: '',
            },
            messages: {
                itemDeleted: '',
                unsavedChangesHeader: '',
                discardChanges: '',
                returnToPage: '',
                saveSuccess: '',
                unsavedChangesMessage: '',
            },
        },
    },
    cancelTrial: {
        errors: {
            failureCancelSubscription: '',
        },
        text: {
            cancelTrialMessage: '',
            cancelTrialHeader: '',
            userSuccessfullyCancelledSubscription: '',
        },
    },
    companies: {
        title: {
            add: '',
            list: '',
            companyUsers: '',
        },
        tabs: {
            billing: '',
            details: '',
            licenses: '',
            permissions: '',
        },
        text: {
            companySuccessfullyAdded: '',
            companySuccessfullySaved: '',
            companySuccessfullyUpdated: '',
            successExport: '',
            indicatesRequiredField: '',
            vatNotApplicable: '',
        },
        fields: {
            address1: '',
            address2: '',
            billingAddress1: '',
            billingAddress2: '',
            billingCity: '',
            billingCompanyName: '',
            billingContactName: '',
            billingCountry: '',
            billingCurrency: '',
            billingEmail: '',
            billingFax: '',
            billingPhone: '',
            billingPostalCode: '',
            billingRegion: '',
            billingSameAsCompanyInformation: '',
            billingState: '',
            billingVatNumber: '',
            city: '',
            company: '',
            contactPerson: '',
            country: '',
            dateAdded: '',
            futuresAccountCustomerName: '',
            futuresAccountNumber: '',
            futuresAccountTitle: '',
            futuresAccountTransferConsent: '',
            invoiceDate: '',
            notes: '',
            numberOfLicenses: '',
            numberOfLicensesUsed: '',
            numberOfUsers: '',
            paymentType: '',
            policyNumbers: '',
            phone: '',
            postalCode: '',
            subscriptionFrequency: '',
            subscriptionPrice: '',
            state: '',
            companyAdministrator: '',
            termsAndConditionsAccepted: '',
            termsAndConditionsAcceptedAt: '',
            totalLicenses: '',
        },
        errors: {
            addCompanyError: '',
            failureExport: '',
            nameRequired: '',
            updateCompanyError: '',
            requiredField: '',
            saveCompanyError: '',
        },
    },
    companyRegistration: {
        description: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
        phone: '',
        contactPerson: '',
        agreeTo: '',
        termsAndConditionsHeader: '',
        billingAddress1: '',
        billingAddress2: '',
        billingCity: '',
        billingContactName: '',
        billingCompanyName: '',
        billingCountry: '',
        billingPhone: '',
        billingFax: '',
        billingEmail: '',
        billingPostalCode: '',
        billingVatNumber: '',
        billingSameAsSubscriberInformation: '',
        billingState: '',
        paymentWireTransfer: '',
        paymentWireTransferUS: '',
        paymentWireTransferEU: '',
        paymentAutomaticDeduction: '',
        futuresAccountNumber: '',
        futuresAccountCustomerName: '',
        futuresAccountTitle: '',
        futuresAccountTransferConsent: '',
        subscriberBillingDetailsHeader: '',
        subscriberPaymentOptionsHeader: '',
    },
    dashboard: {
        title: '',
        chartName: {
            production: '',
            exports: '',
            margin: '',
        },
        headings: {
            articleHeader: '',
            cmeSummaryTableHeading: '',
            commoditySummaryTable: '',
            globalDairyTradeResultTableHeading: '',
            globalDairyTradeSummaryTableHeading: '',
            globalDairyTradeResultTableSubHeading: '',
            marketIndicatorsHeader: '',
            hedgeMonitorProHeader: '',
            priceValueMatrix: '',
            event: '',
        },
        indicators: {
            title: '',
            manageModalSubHeading: '',
            group: '',
            manage: '',
            single: '',
            availableIndicators: '',
            availableProducts: '',
            selectedIndicators: '',
            selectedProducts: '',
            searchIndicatorPlaceholder: '',
            addOrRemoveLeadingIndicators: '',
            addOrRemoveCommodityIndicators: '',
            addOrRemoveHedgeMonitorProIndicators: '',
            indicatorFrequency: '',
        },
        fields: {
            averageWinningPrice: '',
            contract: '',
            allContracts: '',
            changeInOverallPriceIndex: '',
            percentChangeFromPrevious: '',
            percentChangeFromPreviousEvent: '',
        },
        text: {
            commoditySummaryTableFootnote: '',
            customizeView: '',
            dairyTrader: '',
            dairyBuyerOrEndUser: '',
            fiftiethDecile: '',
            producerOrFarmer: '',
            processorOrCoop: '',
            results: '',
            summary: '',
        },
        latestNews: {
            latest: '',
            marketIntelligenceReports: '',
            readFullArticle: '',
        },
        priceSummary: {
            cme: {
                summaryHeader: '',
                headers: {
                    daily: '',
                    weekly: '',
                    historical: '',
                },
                fields: {
                    currentWeekAverage: '',
                    priorWeekAverage: '',
                    weekVolume: '',
                    currentPrice: '',
                    yearAgo: '',
                    monthAgo: '',
                    weekAgo: '',
                    twoDaysAgo: '',
                    oneDayAgo: '',
                },
            },
            dropdownPrefix: '',
            europeanUnion: {
                summaryHeader: '',
                headers: {
                    weeklyCheeseIndices: '',
                    averageIndex: '',
                    quotationsSummary: '',
                },
            },
        },
    },
    dashboardCustomization: {
        messages: {
            confirmDeleteDashboard: '',
            confirmDeleteWidget: '',
            confirmResetDashboard: '',
            deleteDashboardSuccess: '',
            deleteDashboardError: '',
            updateDashboardSuccess: '',
            updateDashboardError: '',
        },
        text: {
            editView: '',
            maximize: '',
            minimize: '',
        },
        titles: {
            addWidget: '',
            dashboard: '',
            deleteDashboard: '',
            reports: '',
            resetDashboard: '',
            resetDashboardModal: '',
        },
        labels: {
            addNew: '',
            addToDashboard: '',
            duplicate: '',
            default: '',
        },
        tooltips: {
            addNewDashboard: '',
            addNewWidget: '',
            maximumMessage: '',
            removeDashboard: '',
        },
        widget: {
            cloneWidget: '',
            deleteWidget: '',
            renameWidget: '',
        },
    },
    destinationsOrOrigins: {
        subHeader: {
            topOrigin: '',
            topDestination: '',
        },
        fields: {
            country: '',
            volume: '',
            yearOverYearChange: '',
            pastTwelveMonths: '',
            yearToDateChange: '',
            yearToDate: '',
        },
        text: {
            total: '',
            monthly: '',
        },
    },
    disclaimer: {
        title: '',
        paragraph1: '',
        paragraph2: '',
        paragraph3: '',
        paragraph4: '',
        paragraph5: '',
        paragraph6: '',
    },
    domestic: {
        title: '',
    },
    // This is the issue where some usages are plural.
    domestics: {
        title: '',
    },
    exports: {
        title: '',
    },
    featureFlags: {
        title: '',
        text: {
            successUpdated: '',
        },
    },
    futures: {
        title: '',
        titleWithoutOtc: '',
        headers: {
            forwardCurve: '',
            priceCurve: '',
        },
        text: {
            delayedFooter: '',
            realtimeFooter: '',
            priceStudiesPlaceholder: '',
            priceLimitMessage: '',
            searchProductPlaceholder: '',
        },
        fields: {
            askPrice: '',
            bidPrice: '',
            contractMonth: '',
            last: '',
            symbol: '',
            bidSize: '',
            askSize: '',
            volumeTradedToday: '',
            settle: '',
            openInterest: '',
            currency: '',
            open: '',
            openInterestNetChange: '',
            expirationDate: '',
            lastSessionVolume: '',
            previousSettlementPrice: '',
            priorSessionHigh: '',
            priorSessionLow: '',
            settleNetChange: '',
            name: '',
            high: '',
            low: '',
            percentChange: '',
            netChange: '',
            chartControl: '',
            tradeRegistrationVolume: '',
            tradeRegistrationPrice: '',
            lastUpdate: '',
            todaysMidpoint: '',
            priorDayMidpoint: '',
        },
        symbols: {
            'Anhydrous Milk Fat': '',
            'Block Cheese': '',
            Butter: '',
            Cheese: '',
            'Class III Milk': '',
            'Class IV Milk': '',
            'Dry Whey': '',
            'Nonfat Dry Milk': '',
            'NZ Milk Price': '',
            'Skim Milk Powder': '',
            'Skimmed Milk Powder': '',
            'Whey Powder': '',
            'Whole Milk Powder': '',
        },
    },
    hedgeMonitorPro: {
        title: '',
        brandingTitleString: '',
        text: {
            fiftiethDecile: '',
            marketIndicator: '',
            precipiceMomentum: '',
        },
        tooltipMessage: '',
    },
    imports: {
        title: '',
    },
    licenses: {
        title: '',
        text: {
            licenseTableFooter: '',
        },
        error: {
            noUsersMessage: '',
        },
    },
    marketIndicators: {
        brandingTitleString: '',
        hedgeMontiorPro: '',
        marketIndicatorErrorMessage: '',
        multipleFactorsErrorMessage: '',
        singleFactorErrorMessage: '',
        title: '',
        tooltipMessage: '',
    },
    marketIndicatorsManagement: {
        actions: {
            activate: '',
            addIndicators: '',
            clone: '',
            deactivate: '',
            editDetails: '',
            history: '',
        },
        headers: {
            actions: '',
            addFactor: '',
            addIndicators: '',
            chartDisplayName: '',
            cloneIndicators: '',
            commodityName: '',
            commentaryGuid: '',
            dataSource: '',
            dateCreated: '',
            deleteFactor: '',
            deleteFactorGroup: '',
            details: '',
            editFactorGroup: '',
            exchangeAndCommodity: '',
            factorName: '',
            indicatorName: '',
            lastUpdated: '',
            marketName: '',
            numberOfYears: '',
            reviewDate: '',
            reviewExpert: '',
            reviewStatus: '',
            reviewedBy: '',
            comments: '',
            status: '',
            template: '',
        },
        fields: {
            basisLocation: '',
            calculationType: '',
            commodity: '',
            contractMonths: '',
            contractNumber: '',
            correlation: '',
            dataTransformation: '',
            daysToAverage1: '',
            daysToAverage2: '',
            daysToAverage3: '',
            endMonth: '',
            factorGroupName: '',
            factorName: '',
            fundamentalCategory: '',
            imageLink: '',
            longTerm: '',
            longTermDays1: '',
            longTermDays2: '',
            longTermDays3: '',
            manualEntry: '',
            monthsFlatOutlook: '',
            monthsForward: '',
            monthsInverseCorrelation: '',
            monthsOfData: '',
            numberOfYears: '',
            oneYearWeightedPercent: '',
            outlook: '',
            periodicity: '',
            product: '',
            region: '',
            shortTermContractNumber: '',
            shortTermDays1: '',
            shortTermDays2: '',
            shortTermDays3: '',
            startMonth: '',
            subRegion: '',
            templateType: '',
            thresholdValue: '',
            transformationValue: '',
            windowEndDay: '',
            windowStartDay: '',
            yearsOfData: '',
        },
        messages: {
            articleNoLongerAvailable: '',
            displayNameInvalid: '',
            factorGroupNameUpdateFailed: '',
            factorGroupNameUpdateSuccessful: '',
            factorUpdateFailed: '',
            factorUpdateSuccessful: '',
            indicatorAddSuccessful: '',
            indicatorAddFailed: '',
            indicatorAddConflict: '',
            indicatorCloneConflict: '',
            indicatorCloneSuccessful: '',
            indicatorCloneFailed: '',
            indicatorUpdateSuccessful: '',
            indicatorUpdateFailed: '',
            marketIndicatorFactorAddFailed: '',
            marketIndicatorFactorAddSuccessful: '',
            marketIndicatorFactorDeleteSuccessful: '',
            marketIndicatorFactorDeleteFailed: '',
            marketIndicatorFactorUpdateFailed: '',
            marketIndicatorFactorUpdateSuccessful: '',
            marketIndicatorReviewAddFailed: '',
            marketIndicatorReviewAddSuccessful: '',
            positiveOrNegativeNumber: '',
            numberOfYearsInvalid: '',
            positiveNumber: '',
            positiveNumberZeroThroughOneHundred: '',
        },
        reviewForm: {
            approve: '',
            approvedBy: '',
            comment: '',
            commentError: '',
            contentReview: '',
            description: '',
            reject: '',
            rejectedBy: '',
            title: '',
        },
        reviewHistory: {
            approvalHistory: '',
            commodity: '',
            noData: '',
            reviewers: '',
        },
        reviewStatus: {
            approved: '',
            expired: '',
            expireSoon: '',
            rejected: '',
        },
        text: {
            addDataSourceSection: '',
            addFactorGroup: '',
            areYouSureDeleteFactor: '',
            areYouSureDeleteFactorGroup: '',
            currentForecast: '',
            currentForwardCurve: '',
            dataTransformationTooltip: '',
            newFactorGroup: '',
            viewCommentary: '',
            viewGuageChart: '',
        },
        title: '',
    },
    murexInstrumentMapping: {
        title: '',
        fields: {
            contractSize: '',
            instrumentKey: '',
            murexMarket: '',
            murexKeyOrRicPrefix: '',
            murexProduct: '',
            reason: '',
            sourceSystem: '',
            stoneXInstrumentId: '',
            subExchange: '',
        },
        status: {
            Mapped: '',
            Unmapped: '',
        },
        actions: {
            allExchanges: '',
            allMarkets: '',
            allProducts: '',
            allStatuses: '',
            clearMapping: '',
            editMapping: '',
            includeInactive: '',
            selectAllOnPage: '',
        },
        text: {
            clearMappingConfirmation: '',
            clearMappingHeader: '',
            editMappingHeader: '',
            mappingCleared: '',
            mappingUpdated: '',
            noUnmappedInstruments: '',
            selectInstrumentsHint: '',
            selected: '',
            unmappedInstrumentsHeader: '',
        },
        errors: {
            clearMappingFailed: '',
            updateMappingFailed: '',
        },
    },
    outlookAndReports: {
        title: '',
        [OutlookAndReportsTitleEnum.AustraliaExports]: '',
        [OutlookAndReportsTitleEnum.AustraliaProduction]: '',
        [OutlookAndReportsTitleEnum.ChineseImports]: '',
        [OutlookAndReportsTitleEnum.ColdStorageReport]: '',
        [OutlookAndReportsTitleEnum.CommitmentOfTradersReport]: '',
        [OutlookAndReportsTitleEnum.DailyReports]: '',
        [OutlookAndReportsTitleEnum.DairyProductsReport]: '',
        [OutlookAndReportsTitleEnum.EarlyMorniningUpdateUnitedStates]: '',
        [OutlookAndReportsTitleEnum.EuropeanUnionAndUnitedKingdomExports]: '',
        [OutlookAndReportsTitleEnum.EuropeanUnionAndUnitedKingdomProduction]: '',
        [OutlookAndReportsTitleEnum.EuropeanUnionCheeseIndices]: '',
        [OutlookAndReportsTitleEnum.EuropeanUnionQuotations]: '',
        [OutlookAndReportsTitleEnum.EuropeanUnionOceaniaDaily]: '',
        [OutlookAndReportsTitleEnum.EuropeanUnionWeeklySummary]: '',
        [OutlookAndReportsTitleEnum.EuropeanUnionWeeklySummaryFr]: '',
        [OutlookAndReportsTitleEnum.EuropeanUnionWeeklySummaryDe]: '',
        [OutlookAndReportsTitleEnum.EuropeanUnionWeeklySummaryPl]: '',
        [OutlookAndReportsTitleEnum.Exports]: '',
        [OutlookAndReportsTitleEnum.GlobalDairyTradeReport]: '',
        [OutlookAndReportsTitleEnum.GlobalOutlook]: '',
        [OutlookAndReportsTitleEnum.GlobalSlideDeck]: '',
        [OutlookAndReportsTitleEnum.Imports]: '',
        [OutlookAndReportsTitleEnum.InternationalButterSpreads]: '',
        [OutlookAndReportsTitleEnum.InternationalWMPSpreads]: '',
        [OutlookAndReportsTitleEnum.InternationalSMPSpreads]: '',
        [OutlookAndReportsTitleEnum.InternationalWheySpreads]: '',
        [OutlookAndReportsTitleEnum.MilkPriceEquivalent]: '',
        [OutlookAndReportsTitleEnum.MilkPriceEquivalentFr]: '',
        [OutlookAndReportsTitleEnum.MilkProduction]: '',
        [OutlookAndReportsTitleEnum.MonthlyOutlooks]: '',
        [OutlookAndReportsTitleEnum.MonthlyWebinarReplay]: '',
        [OutlookAndReportsTitleEnum.NewZealandExports]: '',
        [OutlookAndReportsTitleEnum.NewZealandProduction]: '',
        [OutlookAndReportsTitleEnum.PostGlobalDairyTradeReport]: '',
        [OutlookAndReportsTitleEnum.PreGlobalDairyTradeReport]: '',
        [OutlookAndReportsTitleEnum.ProfitViewFarmerReport]: '',
        [OutlookAndReportsTitleEnum.SeasonalPriceReport]: '',
        [OutlookAndReportsTitleEnum.UdderIntelligence]: '',
        [OutlookAndReportsTitleEnum.UdderIntelligenceChina]: '',
        [OutlookAndReportsTitleEnum.UnitedStatesExports]: '',
        [OutlookAndReportsTitleEnum.UnitedStatesOutlook]: '',
        [OutlookAndReportsTitleEnum.UnitedStatesProduction]: '',
        [OutlookAndReportsTitleEnum.Video]: '',
        [OutlookAndReportsTitleEnum.WeeklyReports]: '',
        [OutlookAndReportsTitleEnum.WeeklyMarketRecapChina]: '',
        [OutlookAndReportsTitleEnum.WeeklyPrices]: '',
        [OutlookAndReportsTitleEnum.WeeklyVideo]: '',
    },
    physicalPrices: {
        title: '',
        chartTypes: {
            lineChart: '',
            areaChart: '',
            column: '',
            spline: '',
        },
        fields: {
            name: '',
            date: '',
            price: '',
            percentChange: '',
            netChange: '',
            previousPrice: '',
            currency: '',
            unit: '',
            frequencyOrForcast: '',
            chartControl: '',
        },
        tooltip: {
            daily: '',
            dailyForecast: '',
            weekly: '',
            weeklyForecast: '',
            monthly: '',
            monthlyForecast: '',
        },
        text: {
            forecastData: '',
            futuresForwardCurve: '',
            limitMessage: '',
            studies: '',
        },
    },
    dataFrequency: {
        Daily: '',
        Monthly: '',
        Streaming: '',
        Weekly: '',
        Yearly: '',
    } as { [key in keyof typeof DemeterDataFrequency]: string },
    presentationTemplates: {
        title: {
            add: '',
            edit: '',
            list: '',
        },
        text: {
            description: '',
            confirmAddHeader: '',
            confirmDeleteHeader: '',
            confirmDeleteMessage: '',
            confirmDuplicateHeader: '',
            confirmShareHeader: '',
            emptyContent: '',
            includeDescription: '',
            includeInExport: '',
            moreOptions: '',
            namePlaceholder: '',
            noImage: '',
            shareDescription: '',
            templateContentHeader: '',
        },
        actions: {
            add: '',
            addToPresentationTemplate: '',
            chooseContent: '',
            edit: '',
            duplicate: '',
            delete: '',
            download: '',
            save: '',
        },
        fields: {
            createdBy: '',
            lastRunAt: '',
            search: '',
            templateFormat: '',
            templateName: '',
            users: '',
        },
        errors: {
            addPresentationTemplateError: '',
            getPresentationTemplateError: '',
            getDemeterPageElementImageError: '',
            listPresentationTemplatesError: '',
            updatePresentationTemplateError: '',
            templateNameRequired: '',
        },
        success: {
            addPresentationTemplateSuccess: '',
            updatePresentationTemplateSuccess: '',
        },
    },
    positions: {
        title: '',
        text: {
            successExport: '',
            positions: '',
        },
        fields: {
            commodity: '',
            companyName: '',
            coveragePeriod: '',
            currentEstimatedIndemnity: '',
            currentMarketPrice: '',
            declaredProduction: '',
            effectiveDate: '',
            endorsementDate: '',
            estimatedNetToProducer: '',
            netCoveredVolume: '',
            policyNumber: '',
            premiumPrice: '',
            pricingMethod: '',
            producerPremium: '',
            protectedProduction: '',
            protectionFactor: '',
            triggerPrice: '',
            salesDate: '',
            settlementDate: '',
            subCommodity: '',
            state: '',
        },
        errors: {
            failureExport: '',
        },
    },
    production: {
        title: '',
        marginChart: {
            subtitle: '',
            title: '',
        },
    },
    registration: {
        actions: {
            completeRegistration: '',
            contactUsForMore: '',
            startNow: '',
        },
        errors: {
            missingParameters: '',
            passwordInvalid: '',
            confirmPasswordInvalid: '',
        },
        fields: {
            password: '',
            confirmPassword: '',
            choosePasswordPlaceholder: '',
            confirmPasswordPlaceholder: '',
        },
        success: {
            clickTheButton: '',
            copyright: '',
            header: '',
            paragraph1: '',
            paragraph2: '',
            paragraph3: '',
            paragraph4: '',
            paragraph5: '',
            paragraph6: '',
            subheader: '',
            watchTheTutorial: '',
            whatWillHappenNext: '',
            youWillRecieveEmail: '',
        },
        text: {
            accessLeadingMarketIntelligence: '',
            accessToTheTrial: '',
            accessTradingAccount: '',
            allForecastingIs: '',
            analyzeAllRelevent: '',
            asianUserSuccessfullyRegistered: '',
            bySubmittingThisForm: '',
            callTheNumber: '',
            canIUseStoneX: '',
            chicagoTelephone: '',
            currentlyStonex: '',
            customizedDashboard: '',
            customizeThePlatform: '',
            dairyMarketsInsights: '',
            derivativesContractsTrading: '',
            discoverHowPowerful: '',
            doesPlusHave: '',
            dublinTelephone: '',
            eachExchangeHas: '',
            exportData: '',
            fatAndProtein: '',
            fmmoCalculator: '', // TODO - change this. What is FMMO?
            forecastForTheTop: '',
            forwardSpreadCalculator: '',
            fourTrillionCombined: '',
            frequentlyAskedQuestions: '',
            futuresPrices: '',
            globalDairyTradePrices: '',
            howAreTheForecasts: '',
            howArePricesDetermined: '',
            howMuchDoesStoneX: '',
            howToRequestMore: '',
            importsData: '',
            isTheFuturesInformation: '',
            lengthMessage: '',
            matchingPasswords: '',
            milkAndMajor: '',
            monthlyProduction: '',
            moreThanOneHundred: '',
            moreThanTwenty: '',
            ourDairyGroup: '',
            ourDiverseGlobal: '',
            passwordRequirements: '',
            positionAnalysis: '',
            powerfulAnalysis: '',
            prices: '',
            privacyPolicy: '',
            production: '',
            proprietaryOverTheCounter: '',
            provideYourBusiness: '',
            recieveLiveAndDelayed: '',
            registrationFailed: '',
            registrationSucceeded: '',
            reportsWereViewed: '',
            selectCountry: '',
            singaporeTelephone: '',
            specialCharacterMessage: '',
            startYourFreeTrial: '',
            statisticallyConsistent: '',
            stonexIsAlso: '',
            stoneXIsAnInstitution: '',
            stonexPlusData: '',
            stonexPlusIsAvailable: '',
            theOtcPrices: '',
            toLearnMore: '',
            toDiscoverHowStonex: '',
            toRequestMoreInformation: '',
            tradeData: '',
            userSuccessfullyRegistered: '',
            weCreateBespoke: '',
            weCurrentlyHaveDairy: '',
            welcomeMessage: '',
            whatInformationDoes: '',
            whatLanguageIs: '',
            whereAreWeBased: '',
            whyChooseStonex: '',
            yesStoneXPlusCan: '',
        },
        title: '',
    },
    risk: {
        title: '',
        headers: {
            budget: '',
            budgetAtRisk: '',
            budgetVersesMarket: '',
            classBasedEndorsement: '',
            componentBasedEndorsement: '',
            combinedEndorsement: '',
            combinedNetPosition: '',
            cumulativeValueAtRisk: '',
            forwardCurveAndBasis: '',
            futures: '',
            insuranceQuote: '',
            insuranceQuoteEstimator: '',
            margin: '',
            marginHeatMap: '',
            marginHedgeMap: '',
            marginTrackerChart: '',
            marketPrices: '',
            markToBudgetOfNetCovered: '',
            markToMarket: '',
            markToMarketNetOpenPositions: '',
            markToMarketOfNetCovered: '',
            netPosition: '',
            physical: '',
            physicalPositionNetLong: '',
            physicalPositionNetSales: '',
            physicalPositionSecured: '',
            positions: '',
            productionVersusHedgedVolume: '',
            purchases: '',
            monthlyNetPositionValueAtRisk: '',
            netPositionCumulativeRisk: '',
            netPositionMonthlyRisk: '',
            netUnhedgedPosition: '',
            sales: '',
            stocks: '',
            valueAtRisk: '',
            valueAtRiskAssumptions: '',
        },
        insuranceQuoteEstimator: {
            actions: {
                contactAgent: '',
                subscribe: '',
            },
            commodity: {
                fedCattleType: 'risk.insuranceQuoteEstimator.commodity.fedCattleType',
            },
            headers: {
                cornFields: '',
                coverageLevels: '',
                dairyRevenueProtectionForm: '',
                dairyRevenueProtectionPremiumInformation: '',
                livestockRevenueProtectionForm: '',
                subscribe: '',
            },
            messages: {
                subscriptionFailure: '',
                subscriptionSuccess: '',
            },
            text: {
                coverageLevels: '',
                dailySubscription: '',
                defaultRiskPremium: '',
                fillOutTheForm: '',
                livestockRevenueProtection: '',
                manageSubscriptions: '',
                nameSubscription: '',
                subscription: '',
                textNeeded: '',
                thisIsAQuoteOnly: '',
                weeklySubscription: '',
            },
        },
        margin: {
            title: '',
            fields: {
                animalHeadCount: '',
                butterfatPercentage: '',
                calculator: '',
                class3Percent: '',
                class4Percent: '',
                classBasedPercent: '',
                componentBasedPercent: '',
                company: '',
                costPerPoundOfGrain: '',
                coverageEffectiveDate: '',
                coverageLevel: '',
                declaredComonentPriceWeighting: '',
                declaredCoveredMilkProduction: '',
                declaredShare: '',
                endingQuarter: '',
                endorsementLength: '',
                endWeight: '',
                firstName: '',
                insuredSharedPercentage: '',
                insuranceYear: '',
                lastName: '',
                livestockRevenueProtectionQuote: '',
                location: '',
                protectionFactor: '',
                proteinPercentage: '',
                purchaseCostPerAnimal: '',
                shortWeight: '',
                startingQuarter: '',
                state: '',
                subject: '',
                type: '',
                volatility: '',
            },
            headers: {
                butterFat: '',
                class3Milk: '',
                class4Milk: '',
                classBasedInsurance: '',
                componentBasedInsurance: '',
                componentWeighting: '',
                coverageLevel: '',
                coveragePrice: '',
                customCalculatorCreate: '',
                customCalculatorDelete: '',
                customCalculatorRename: '',
                customCalculatorSavedCalculators: '',
                declaredMilkProduction: '',
                effectiveDate: '',
                endCoverageDate: '',
                endDate: '',
                endorsementLength: '',
                expectedValue: '',
                forgetToSelect: '',
                headCount: '',
                insuredValue: '',
                nonfatSolids: '',
                otherSolids: '',
                premiumAfterSubsidy: '',
                premiumAfterSubsidyPerCwt: '',
                premiumBeforeSubsidy: '',
                premiumBeforeSubsidyCwt: '',
                priceOptionInformation: '',
                producerPremium: '',
                producerCostPerHead: '',
                producerCostPerHundredWeight: '',
                protein: '',
                rate: '',
                revenueGuarentee: '',
                revenueGuarenteePerCwt: '',
                subsidy: '',
                subsidyPerCwt: '',
                totalPremium: '',
                type: '',
                yield: '',
            },
            messages: {
                contactBrokerFailure: '',
                contactBrokerSuccess: '',
                customCalculatorConfirmDelete: '',
                customCalculatorDeleteSuccess: '',
                customCalculatorNoSaved: '',
            },
            text: {
                addLot: '',
                adjustedCoveragePrice: '',
                bias: '',
                breakEven: '',
                breakEvenShortText: '',
                butterNonFatDryMilk: '',
                calculatorAddedSuccessfully: '',
                calculatorUpdatedSuccessfully: '',
                cheeseWhey: '',
                classBased: '',
                componentBased: '',
                costTotalPrice: '',
                coveragePrice: '',
                currentMarketPrice: '',
                customCalculatorDelete: '',
                customCalculatorCloneCurrent: '',
                customCalculatorNewEmpty: '',
                customCalculatorNone: '',
                dairyRevenueProtection: '',
                editProducts: '',
                endorsedQuantity: '',
                ensureLotName: '',
                futures: '',
                futuresPrice: '',
                futuresBlendPrice: '',
                headInsured: '',
                hedgedMargin: '',
                hedgedVolume: '',
                marketPrice: '',
                marketPriceShortText: '',
                milkFutures: '',
                options: '',
                physicallyContracted: '',
                production: '',
                productionInformation: '',
                protectedQuantity: '',
                removeAllLots: '',
                productsForFutures: '',
                severityOfCoverage: '',
                subscription: '',
                toContactAgent: '',
                unhedgedMargin: '',
                weightedAverageCoveragePrice: '',
            },
        },
        text: {
            basis: '',
            budgetMessage: '',
            combinedNetPosition: '',
            declaredShareMessage: '',
            disclaimer: '',
            fatPercentageMessage: '',
            futuresPosition: '',
            futuresPositionMessage: '',
            futuresPrice: '',
            historicVolatility: '',
            marginAtRisk: '',
            marketPrices: '',
            markToMarket: '',
            markToMarketAdjusted: '',
            markToMarketPriceDifference: '',
            markToMarketReference: '',
            markToMarketTotal: '',
            monthlyBudgetAtRisk: '',
            netFuturesSwapPosition: '',
            physicalPositionMessage: '',
            physicalPositionNetSales: '',
            physicalPositionNetLong: '',
            price: '',
            proteinPercentageMessage: '',
            netPhysicalPosition: '',
            netPosition: '',
            nettedVolume: '',
            netUnhedgedPosition: '',
            netUnhedgedPrice: '',
            openingPrice: '',
            openingWeightedAveragePrice: '',
            openingVolume: '',
            perUnit: '',
            physicalPositionNetLongPosition: '',
            physicalPositionNetLongPrice: '',
            physicalPositionNetSalesPosition: '',
            physicalPositionNetSalesPrice: '',
            physicalPositionSecuredPosition: '',
            physicalPositionSecuredPrice: '',
            physicalPositionSecuredTotal: '',
            physicalPosition: '',
            physicalPrice: '',
            purchasesPosition: '',
            purchasesPrice: '',
            reference: '',
            riskCumulative: '',
            riskDays: '',
            riskMonthly: '',
            salesPosition: '',
            salesPrice: '',
            physicalPositionSecured: '',
            stocksPosition: '',
            stocksPrice: '',
            stocksUnpricedDrawdown: '',
            stocksUnpricedIncrease: '',
            unpricedDrawdown: '',
            unpricedIncrease: '',
            valueAtRisk: '',
            volatilityPercent: '',
            weightedAveragePrice: '',
        },
    },
    sessionExpiration: {
        clickClosePreviousSession: '',
        forQuestionsContactUs: '',
        loggedInOnAnotherDevice: '',
        reloadSession: '',
        reprimandText1: '',
        reprimandText2: '',
    },
    stocks: {
        title: '',
        text: {
            shortCommericalStocks: '',
            shortGovernmentStocks: '',
        },
    },
    stocksCommercial: {
        title: '',
    },
    stocksGovernment: {
        title: '',
    },
    subscriptions: {
        title: '',
    },
    subscriptionType: {
        DairyInsurancePriceSubscription: '',
        LivestockInsurancePriceSubscription: '',
    } as { [key in keyof typeof DemeterEmailSubscriptionType]: string },
    trialExpiration: {
        benefitsList: {
            analyzeMarketData: '',
            buildCompletePicture: '',
            developUnderstanding: '',
            fullyCustomize: '',
            gainAccessInsights: '',
            keepTrack: '',
            stayUpToDate: '',
        },
        header: {
            benefitsOfSubscribing: '',
            hopeYouEnjoyed: '',
            apologizeForInconvenience: '',
        },
        text: {
            email: '',
            stayOneStepAhead: '',
            weTrustThat: '',
            weWillTakeCare: '',
            copyText: '',
            trialExpirationNotice: '',
            errorWhileAccessingContactTeam: '',
            contactTeam: '',
        },
    },
    users: {
        title: {
            add: '',
            list: '',
        },
        text: {
            batchImportWarning: '',
            confirmApproveHeader: '',
            confirmApproveMessage: '',
            confirmDeactivateHeader: '',
            confirmDeactivateMessage: '',
            confirmReactivateHeader: '',
            confirmReactivateMessage: '',
            confirmRejectHeader: '',
            confirmRejectMessage: '',
            confirmSectorsWithoutEndDateHeader: '',
            confirmSectorsWithoutEndDateMessage: '',
            downloadTemplateLink: '',
            confirmResendTrialInvitationHeader: '',
            confirmResendTrialInvitationMessage: '',
            removeMarketIntelligenceMessage: '',
            resendTrialInvitationSuccess: '',
            successExport: '',
            userSuccessfullyCreated: '',
            userSuccessfullyUpdated: '',
            assignLicenses: '',
        },
        actions: {
            approve: '',
            deactivate: '',
            reactivate: '',
            reject: '',
            resendTrialInvitation: '',
            export: '',
            search: '',
            allSectors: '',
        },
        fields: {
            company: '',
            companyAdministrator: '',
            email: '',
            expiration: '',
            firstName: '',
            importStatus: '',
            lastName: '',
            lastUseDate: '',
            license: '',
            marketPrecision: '',
            markets: '',
            name: '',
            notes: '',
            phone: '',
            realtimeExchanges: '',
            role: '',
            status: '',
            subscribedDate: '',
            termsAndConditionsAcceptedAt: '',
        },
        import: {
            failed: '',
            skipped: '',
            success: '',
        },
        errors: {
            addUserError: '',
            companyNameRequired: '',
            emailAlreadyExistsError: '',
            emailInvalid: '',
            failureExport: '',
            firstNameRequired: '',
            lastNameRequired: '',
            marketAccessEndDateAfterAccountExpiration: '',
            marketAccessEndDateNotInFuture: '',
            marketAccessEndDateRequired: '',
            marketAccessGrantLongerThanOneYear: '',
            notCsv: '',
            phoneInvalid: '',
            resendTrialInvitationError: '',
            updateUserError: '',
        },
        marketAccess: {
            accessPeriod: '',
            applyToAllSectors: '',
            dateNeeded: '',
            daysRemaining: '',
            endedOn: '',
            endsOn: '',
            expiresToday: '',
            expiringSoon: '',
            noEndDate: '',
            oneDayRemaining: '',
            pastGrantsTitle: '',
            permanent: '',
            setAccessPeriod: '',
            title: '',
        },
        marketAccessStatus: {
            [DemeterMarketAccessStatus.Active]: '',
            [DemeterMarketAccessStatus.Expired]: '',
        } as { [key in keyof typeof DemeterMarketAccessStatus]: string },
        trialLength: {
            custom: '',
            fourteenDays: '',
            sixtyDays: '',
            thirtyDays: '',
        },
        roles: {
            All: '',
            [DemeterUserType.Administrator]: '',
            [DemeterUserType.BusinessOwner]: '',
            [DemeterUserType.Premium]: '',
            [DemeterUserType.Regular]: '',
        } as { [key in keyof typeof DemeterUserType | 'All']: string },
        status: {
            All: '',
            [DemeterUserStatus.Active]: '',
            [DemeterUserStatus.Deactivated]: '',
            [DemeterUserStatus.Expired]: '',
            [DemeterUserStatus.Expiring]: '',
            [DemeterUserStatus.Pending]: '',
            [DemeterUserStatus.PendingApproval]: '',
            [DemeterUserStatus.Rejected]: '',
        } as { [key in keyof typeof DemeterUserStatus | 'All']: string },
        marketPrecision: {
            [MarketPrecision.Delayed]: '',
            [MarketPrecision.EndOfDay]: '',
            [MarketPrecision.Realtime]: '',
        } as { [key in keyof typeof MarketPrecision]: string },
    },
    maintenance: {
        title: '',
        headers: {
            cache: '',
            monitoring: '',
        },
        actions: {
            forceRefreshCache: '',
            disableFrontendCache: '',
            enableConsoleDebug: '',
        },
    },
    workers: {
        title: '',
        fields: {
            name: '',
            lastRun: '',
            lastStatus: '',
            lastSuccessDate: '',
            lastFailureDate: '',
            lastFailureMessage: '',
        },
    },
    analytics: {
        title: '',
        toolbar: {
            reportType: {
                placeholder: '',
            },
        },
    },
    modalChartFooterDisclaimer: '',
    pageFooterDisclaimer: '',
    privacyPolicyHeader: '',
    privacyPolicyContents: '',
};

export type TranslationType = typeof TranslationKeyDefinition;

const translationKeysInternal = {} as unknown as TranslationType;
const convertObjectToTranslation = (namespace: string[], source: any, destination: any): void => {
    Object.keys(source).forEach((key) => {
        const newNamespace = [...namespace, key];
        const value = source[key];

        if (typeof value === 'string') {
            destination[key] = newNamespace.join('.');
            return;
        }

        destination[key] = {};
        convertObjectToTranslation(newNamespace, value, destination[key]);
    });
};

convertObjectToTranslation([], TranslationKeyDefinition, translationKeysInternal);

export const translationKeys = translationKeysInternal;

export default TranslationKeyDefinition;
